import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Edit,
  Loader2,
  Clock,
  User,
  Calendar,
  Store,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emailStatusText = (email) => {
  if (email?.status === 'sent') return `Email sent to ${email.to}.`;
  if (email?.status === 'mock') return 'Email not sent (email not configured).';
  return 'Email notification could not be sent.';
};

const ACTION_UI = {
  approve: {
    title: 'Approve Request', color: 'text-green-600', icon: <CheckCircle2 className="h-5 w-5" />, confirm: 'Approve Request',
    description: 'The entry stays read-only. Staff (or an admin) submit it using "Submit Approved Entry" on the Sales Entry screen. Your comments are shown to staff and included in the email.',
    placeholder: 'e.g., Approved - variance due to pump calibration',
  },
  edit_submit: {
    title: 'Edit & Submit', color: 'text-blue-600', icon: <Edit className="h-5 w-5" />, confirm: 'Continue to Edit & Submit',
    description: 'You will edit and submit the entry yourself. Your comments are saved when you submit.',
    placeholder: 'e.g., Corrected fuel volumes before submitting',
  },
  reject: {
    title: 'Reject Request', color: 'text-red-600', icon: <XCircle className="h-5 w-5" />, confirm: 'Reject Request',
    description: 'Please provide a reason for rejecting this variance request. The staff will be able to edit and resubmit the entry.',
    placeholder: "e.g., Please check the fuel volumes - they don't match the pump readings",
  },
};

const formatShift = (shift) => {
  const norm = (shift || '').toLowerCase().replace(/[_\s]/g, '');
  return { dayend: 'Day End', midday: 'Mid Day' }[norm] || shift;
};

const ApprovalRequestsPage = () => {
  const { user, isAdmin, isSuperUser } = useAuth();
  const { stores } = useStore();
  const navigate = useNavigate();
  
  // Only admins can access this page
  const canAccess = isAdmin || isSuperUser;
  
  const [pendingRequests, setPendingRequests] = useState([]);
  const [completedRequests, setCompletedRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [actionMode, setActionMode] = useState('reject');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Fetch requests on mount
  useEffect(() => {
    if (canAccess) {
      fetchRequests();
    }
  }, [canAccess]);
  
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const [pendingRes, completedRes] = await Promise.all([
        axios.get(`${API}/approval-requests`, { params: { status: 'pending' } }),
        axios.get(`${API}/approval-requests`, { params: { status: 'completed' } })
      ]);
      
      setPendingRequests(pendingRes.data.requests || []);
      setCompletedRequests(completedRes.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to load approval requests');
    } finally {
      setLoading(false);
    }
  };
  
  // One comments dialog for Approve / Edit & Submit / Reject
  const openActionDialog = (request, mode) => {
    setSelectedRequest(request);
    setActionMode(mode);
    setRejectReason('');
    setRejectDialogOpen(true);
  };
  const handleApprove = (request) => openActionDialog(request, 'approve');
  const openRejectDialog = (request) => openActionDialog(request, 'reject');
  const handleEditAndSubmit = (request) => openActionDialog(request, 'edit_submit');

  const handleConfirmAction = async () => {
    const comment = rejectReason.trim();
    if (actionMode === 'reject' && !comment) {
      toast.error('Please provide a rejection reason');
      return;
    }
    if (actionMode === 'edit_submit') {
      // Comment is saved when the admin actually submits the entry
      sessionStorage.setItem(`approval_notes_${selectedRequest.id}`, comment || 'Admin edited and submitted the entry');
      setRejectDialogOpen(false);
      const r = selectedRequest;
      navigate(`/sales-entry?store_id=${r.store_id}&date=${r.sales_date}&shift=${r.shift}&approval_request_id=${r.id}&mode=admin_edit`);
      return;
    }
    setProcessing(true);
    try {
      if (actionMode === 'approve') {
        const res = await axios.patch(`${API}/approval-requests/${selectedRequest.id}/approve`, { notes: comment || 'Approved by admin' });
        toast.success(`Request approved. Staff can now submit it using "Submit Approved Entry". ${emailStatusText(res.data.email)}`);
      } else {
        const res = await axios.patch(`${API}/approval-requests/${selectedRequest.id}/reject`, { reason: comment });
        toast.success(`Request rejected. Staff can now edit and resubmit. ${emailStatusText(res.data.email)}`);
      }
      setRejectDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionMode} request`);
    } finally {
      setProcessing(false);
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval':
        return <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Approved - Awaiting Staff Submit</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'resolved_by_admin':
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30"><Edit className="h-3 w-3 mr-1" />Resolved by Admin</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Approved &amp; Submitted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  // Access denied for non-admins
  if (!canAccess) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-500/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-500">
              <AlertTriangle className="h-6 w-6" />
              <div>
                <h2 className="font-semibold">Access Denied</h2>
                <p className="text-sm text-muted-foreground">Only Administrators can access this page.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const RequestCard = ({ request, showActions = true }) => (
    <Card className="mb-3" data-testid={`approval-request-card-${request.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Request Info */}
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-lg">{request.store_name}</span>
              {getStatusBadge(request.status)}
            </div>
            
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(request.sales_date + 'T00:00:00'), 'dd MMM yyyy')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatShift(request.shift)}
              </span>
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {request.requested_by_name}
              </span>
            </div>
            
            {/* Variance Details */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-2">
              <div className="flex items-center gap-2 text-amber-600 font-medium mb-1">
                <TrendingUp className="h-4 w-4" />
                Max Variance: £{Math.abs(request.variance_amount || 0).toFixed(2)}
              </div>
              {request.variance_details?.checks?.length > 0 ? (
                <div className="space-y-1 text-sm">
                  {request.variance_details.checks.map(c => (
                    <div key={c.key} className="flex flex-wrap gap-x-3 text-muted-foreground" data-testid={`request-check-${request.id}-${c.key}`}>
                      <span className="font-medium text-foreground">{c.label}:</span>
                      <span>{c.enteredLabel} £{(c.entered || 0).toFixed(2)}</span>
                      <span>{c.expectedLabel} £{(c.expected || 0).toFixed(2)}</span>
                      <span className="text-red-500 font-mono">Variance £{(c.variance || 0).toFixed(2)} (limit ±£{c.limit.toFixed(2)})</span>
                    </div>
                  ))}
                </div>
              ) : request.variance_details && (
                <div className="text-sm text-muted-foreground">
                  <span>Entered: £{request.variance_details.entered?.toFixed(2) || '0.00'}</span>
                  <span className="mx-2">|</span>
                  <span>Calculated: £{request.variance_details.calculated?.toFixed(2) || '0.00'}</span>
                </div>
              )}
            </div>
            
            {request.staff_comment && (
              <div className="text-sm p-2 rounded-md bg-muted/50" data-testid={`request-staff-comment-${request.id}`}>
                <span className="font-medium">Staff comment: </span>{request.staff_comment}
              </div>
            )}

            {/* Timestamps */}
            <div className="text-xs text-muted-foreground">
              Requested: {request.requested_at && format(new Date(request.requested_at), 'dd/MM/yyyy HH:mm')}
              {request.resolved_at && (
                <> | Resolved: {format(new Date(request.resolved_at), 'dd/MM/yyyy HH:mm')} by {request.resolved_by_name}</>
              )}
            </div>
            
            {/* Resolution Notes */}
            {request.resolution_notes && (
              <div className="text-sm bg-muted/50 rounded p-2 mt-2">
                <span className="font-medium">Notes: </span>
                {request.resolution_notes}
              </div>
            )}
          </div>
          
          {/* Actions */}
          {showActions && request.status === 'pending_approval' && (
            <div className="flex flex-col gap-2 min-w-[140px]">
              <Button 
                size="sm" 
                onClick={() => handleApprove(request)}
                data-testid={`approve-btn-${request.id}`}
                disabled={processing}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleEditAndSubmit(request)}
                data-testid={`edit-submit-btn-${request.id}`}
                disabled={processing}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit & Submit
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => openRejectDialog(request)}
                data-testid={`reject-btn-${request.id}`}
                disabled={processing}
                className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
  
  return (
    <div className="container mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-amber-500" />
            Approval Requests
          </h1>
          <p className="text-muted-foreground text-sm">Review and approve sales variance requests (Admin Only)</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRequests} disabled={loading} data-testid="refresh-requests-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="relative" data-testid="pending-requests-tab">
            Pending
            {pendingRequests.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {pendingRequests.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="completed-requests-tab">Completed</TabsTrigger>
        </TabsList>
        
        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : pendingRequests.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-50" />
                <h3 className="font-medium text-lg">No Pending Requests</h3>
                <p className="text-muted-foreground text-sm">All approval requests have been processed.</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {pendingRequests.map(request => (
                <RequestCard key={request.id} request={request} showActions={true} />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Completed Tab */}
        <TabsContent value="completed" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : completedRequests.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <h3 className="font-medium text-lg">No Completed Requests</h3>
                <p className="text-muted-foreground text-sm">Completed requests will appear here.</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {completedRequests.map(request => (
                <RequestCard key={request.id} request={request} showActions={false} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Action (Approve / Edit & Submit / Reject) Comments Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent data-testid={`action-dialog-${actionMode}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${ACTION_UI[actionMode].color}`}>
              {ACTION_UI[actionMode].icon}
              {ACTION_UI[actionMode].title}
            </DialogTitle>
            <DialogDescription>{ACTION_UI[actionMode].description}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedRequest && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <div className="font-medium">{selectedRequest.store_name}</div>
                <div className="text-muted-foreground">
                  {format(new Date(selectedRequest.sales_date + 'T00:00:00'), 'dd MMM yyyy')} - {formatShift(selectedRequest.shift)}
                </div>
                <div className="text-amber-600 font-medium mt-1">
                  Variance: £{Math.abs(selectedRequest.variance_amount || 0).toFixed(2)}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="action-comment">{actionMode === 'reject' ? 'Rejection Reason *' : 'Comments (optional)'}</Label>
              <Textarea
                id="action-comment"
                placeholder={ACTION_UI[actionMode].placeholder}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                data-testid="action-comment-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={processing} data-testid="action-cancel-btn">
              Cancel
            </Button>
            <Button 
              variant={actionMode === 'reject' ? 'destructive' : 'default'}
              onClick={handleConfirmAction}
              data-testid="action-confirm-btn"
              disabled={processing || (actionMode === 'reject' && !rejectReason.trim())}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {ACTION_UI[actionMode].confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalRequestsPage;
